import React, { useState, useEffect } from "react";
import HPageContainer from "src/hcomponents/HPageContainer";
import HHeader from "src/hcomponents/HHeader";
import { CameraIcon, UploadIcon, Image as LucideImage, Eye, EyeOff, Trash2 } from "lucide-react";
import apiV2Service, { organizationApiService } from "src/services/apiV2Service";
import spaceManagementService from "src/services/host-apiServices/spaceManagementService";
import { showApiMessage } from "src/utils/messageHandler";
import CountryCurrencyDisplay from "src/components/CountryCurrencyDisplay";
import logger from "../../../utils/logger";


const UserProfile = () => {
  // Profile state
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    msisdn: "",
    imageUrl: "",
    username: "",
    organizationRole: null,
    emailConfirmed: true,
  });
  const [_profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(true);
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // Organization state
  const [organization, setOrganization] = useState({
    id: null,
    name: "",
    code: "",
    contactEmail: "",
    alternateEmail: "",
    contactMsisdn: "",
    address: "",
    city: "",
    region: "",
    logoUrl: "",
    domain: "",
    webUrl: "",
    status: "",
    type: "",
    country: "",
  });
  const [organizationLoading, setOrganizationLoading] = useState(true);
  const [organizationError, setOrganizationError] = useState("");
  const [organizationSaving, setOrganizationSaving] = useState(false);
  const [organizationSuccess, setOrganizationSuccess] = useState(false);
  const [showOrganizationEdit, setShowOrganizationEdit] = useState(true);
  const [_userOrganizationId, setUserOrganizationId] = useState(null);

  // Favorites state (unchanged)
  const [favorites, setFavorites] = useState(["Meeting Room A", "Open Desk 12", "Conference Hall"]);
  const [inputValue, setInputValue] = useState("");

  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess(false);
    try {
      const response = await apiV2Service.getSelfUser();
      const user = response.data;
      setProfile({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        emailAddress: user.emailAddress || "",
        msisdn: user.msisdn || "",
        imageUrl: user.imageUrl || "",
        username: user.username || "",
        organizationRole: user.organizationRole || null,
        emailConfirmed: user.emailConfirmed ?? true,
      });

      // Get organization ID from user data
      if (user.organizationId) {
        setUserOrganizationId(user.organizationId);
        // Fetch organization details after getting the ID
        fetchOrganization(user.organizationId);
      }
    } catch (error) {
      setProfileError("Failed to load profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setProfileSuccess(false);
  };

  const handleProfileSave = async e => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError("");
    setProfileSuccess(false);
    try {
      // Remove organizationRole and username from the update payload as they are read-only
      const { organizationRole, username, emailConfirmed, ...updatePayload } = profile;
      const response = await apiV2Service.updateSelfUser(updatePayload);

      if (response.status === 200 || response.status === 204) {
        const updatedUser = response.data?.data || response.data;
        if (updatedUser) {
          // Update local service for global persistence
          const { saveUser } = await import("src/services/localService");
          saveUser(updatedUser);

          // Refresh local profile state
          setProfile({
            firstName: updatedUser.firstName || "",
            lastName: updatedUser.lastName || "",
            emailAddress: updatedUser.emailAddress || "",
            msisdn: updatedUser.msisdn || "",
            imageUrl: updatedUser.imageUrl || "",
            username: updatedUser.username || "",
            organizationRole: updatedUser.organizationRole || null,
            emailConfirmed: updatedUser.emailConfirmed ?? true,
          });

          // Dispatch event for other components (like layouts) to refresh
          window.dispatchEvent(new CustomEvent("currentUserUpdated", { detail: updatedUser }));
        }
        showApiMessage(response, "update profile");
        setProfileSuccess(true);
      }
    } catch (error) {
      showApiMessage(error, "update profile");
      setProfileError("Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProfileImage(true);
    try {
      const response = await spaceManagementService.uploadMedia([file]);
      if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
        const newImageUrl = response.data[0];
        setProfile(prev => ({ ...prev, imageUrl: newImageUrl }));
        // Immediately update profile with new imageUrl (remove organizationRole, username, and emailConfirmed from payload)
        const { organizationRole, username, emailConfirmed, ...profileWithoutRole } = profile;
        const updatedProfile = { ...profileWithoutRole, imageUrl: newImageUrl };
        const updateResponse = await apiV2Service.updateSelfUser(updatedProfile);
        showApiMessage(updateResponse, "update profile image");
        setProfileSuccess(true);
      } else {
        showApiMessage({ response: { status: 400, data: { message: "Failed to upload image" } } }, "upload image");
        setProfileError("Failed to upload image");
      }
    } catch (error) {
      showApiMessage(error, "upload image");
      setProfileError("Failed to upload image");
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setUploadingProfileImage(true);
    try {
      const { organizationRole, username, emailConfirmed, ...profileWithoutRole } = profile;
      const updatedProfile = { ...profileWithoutRole, imageUrl: "" };
      const response = await apiV2Service.updateSelfUser(updatedProfile);
      setProfile(prev => ({ ...prev, imageUrl: "" }));
      showApiMessage(response, "delete profile image");
      setProfileSuccess(true);
    } catch (error) {
      showApiMessage(error, "delete image");
      setProfileError("Failed to delete image");
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordError("");
    setPasswordSuccess(false);
  };

  const togglePasswordVisibility = field => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword.trim()) {
      setPasswordError("Current password is required");
      return false;
    }

    if (!newPassword.trim()) {
      setPasswordError("New password is required");
      return false;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return false;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return false;
    }

    return true;
  };

  const handlePasswordSave = async e => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setPasswordSaving(true);
    setPasswordError("");
    setPasswordSuccess(false);

    try {
      const response = await apiV2Service.updateSelfPassword({
        password: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "update password");
        setPasswordSuccess(true);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        showApiMessage(
          { response: { status: response.status, data: { message: "Failed to update password" } } },
          "update password"
        );
        setPasswordError("Failed to update password");
      }
    } catch (error) {
      logger.error("Password update error:", error);
      showApiMessage(error, "update password");
      if (error.response?.status === 400) {
        setPasswordError("Current password is incorrect");
      } else if (error.response?.status === 401) {
        setPasswordError("Unauthorized. Please log in again");
      } else {
        setPasswordError("Failed to update password. Please try again");
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const fetchOrganization = async organizationId => {
    if (!organizationId) {
      setOrganizationError("No organization ID available");
      setOrganizationLoading(false);
      return;
    }

    setOrganizationLoading(true);
    setOrganizationError("");
    setOrganizationSuccess(false);
    try {
      const response = await organizationApiService.getOrganizationById(organizationId);
      const orgData = response.data?.data || response.data;
      setOrganization({
        id: orgData.id || null,
        name: orgData.name || "",
        code: orgData.code || "",
        contactEmail: orgData.contactEmail || "",
        alternateEmail: orgData.alternateEmail || "",
        contactMsisdn: orgData.contactMsisdn || "",
        address: orgData.address || "",
        city: orgData.city || "",
        region: orgData.region || "",
        logoUrl: orgData.logoUrl || "",
        domain: orgData.domain || "",
        webUrl: orgData.webUrl || "",
        status: orgData.status || "",
        type: orgData.type || "",
        country: orgData.country || "",
      });
    } catch (error) {
      setOrganizationError("Failed to load organization details");
    } finally {
      setOrganizationLoading(false);
    }
  };

  const handleOrganizationChange = (field, value) => {
    setOrganization(prev => ({ ...prev, [field]: value }));
    setOrganizationSuccess(false);
  };

  const handleOrganizationSave = async e => {
    e.preventDefault();
    setOrganizationSaving(true);
    setOrganizationError("");
    setOrganizationSuccess(false);
    try {
      const updatePayload = {
        name: organization.name,
        code: organization.code,
        contactEmail: organization.contactEmail,
        alternateEmail: organization.alternateEmail,
        contactMsisdn: organization.contactMsisdn,
        address: organization.address,
        city: organization.city,
        region: organization.region,
        logoUrl: organization.logoUrl,
        domain: organization.domain,
        webUrl: organization.webUrl,
      };

      const response = await organizationApiService.updateSelfOrganization(updatePayload);
      showApiMessage(response, "update organization");
      setOrganizationSuccess(true);
    } catch (error) {
      showApiMessage(error, "update organization");
      setOrganizationError("Failed to update organization details");
    } finally {
      setOrganizationSaving(false);
    }
  };

  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const handleResendEmail = async () => {
    const username = profile.username || profile.emailAddress;
    if (!username) return;

    setIsResendingEmail(true);
    try {
      const response = await apiV2Service.resendEmailConfirmation(username);
      showApiMessage(response, "resend verification email");
    } catch (error) {
      showApiMessage(error, "resend verification email");
    } finally {
      setIsResendingEmail(false);
    }
  };

  // Favorites logic (unchanged)
  const _addFavorite = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !favorites.includes(trimmed)) {
      setFavorites([...favorites, trimmed]);
      setInputValue("");
    }
  };
  const _removeFavorite = name => {
    setFavorites(favorites.filter(fav => fav !== name));
  };

  // Check if company section should be hidden based on organization role
  const shouldHideCompanySection = () => {
    if (!profile.organizationRole) return false;

    const roleCode = profile.organizationRole.code;
    // Hide organization section for host individual users and other customer roles
    const hideRoles = [
      "ROLE_HOST_CUSTOMER_INDIVIDUAL",
      "ROLE_SUBSCRIBER_HOST",
      "ROLE_SUBSCRIBER_CUSTOMER",
      "ROLE_CORPORATE_CUSTOMER",
      "ROLE_SUBSCRIBER_HOST_INDIVIDUAL",
    ];

    return hideRoles.includes(roleCode);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const response = await apiV2Service.deleteUserAccount();
      if (response.status === 204 || response.status === 200) {
        localStorage.clear();
        window.location.href = "/signin";
      }
    } catch (error) {
      showApiMessage(error, "delete account");
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteAccountModal(false);
    }
  };

  return (
    <>
      <HPageContainer>
        <HHeader />
        <div className="overflow-auto flex-1 p-3 w-full h-full sm:p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-screen-7xl">
            <div
              className={`grid grid-cols-1 gap-6 ${shouldHideCompanySection() ? "lg:grid-cols-2" : "lg:grid-cols-2 xl:grid-cols-3"}`}
            >
              {/* Personal Information */}
              <div className="p-4 bg-white rounded-xl border border-gray-600">
                <h2 className="mb-3 text-sm font-semibold">Personal Information</h2>
                {/* Profile Photo Section */}
                <div className="flex gap-4 items-center mb-4">
                  <div className="relative w-20 h-20">
                    {profile.imageUrl ? (
                      <img
                        src={profile.imageUrl}
                        alt="Profile"
                        className="object-cover w-full h-full rounded-full border"
                      />
                    ) : (
                      <LucideImage className="absolute top-1/2 left-1/2 w-10 h-10 text-gray-300 -translate-x-1/2 -translate-y-1/2" />
                    )}
                    <label className="absolute right-0 bottom-0 p-1 text-white bg-black rounded-full cursor-pointer">
                      <CameraIcon className="w-4 h-4" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfileImageUpload}
                        disabled={uploadingProfileImage}
                      />
                    </label>
                    {uploadingProfileImage && (
                      <div className="flex absolute inset-0 justify-center items-center bg-white bg-opacity-70 rounded-full">
                        <span className="text-xs text-gray-600">Uploading...</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium">Profile Photo</div>
                    <p className="text-xs text-gray-500">
                      Upload a professional photo that represents you well. JPG, PNG or GIF (max 5MB)
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-100 cursor-pointer">
                        <UploadIcon className="w-4 h-4" />
                        {profile.imageUrl ? "Change" : "Upload"} Photo
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleProfileImageUpload}
                          disabled={uploadingProfileImage}
                        />
                      </label>
                      {profile.imageUrl && (
                        <button
                          type="button"
                          onClick={handleDeleteProfileImage}
                          disabled={uploadingProfileImage}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 rounded-md border border-red-200 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <form onSubmit={handleProfileSave}>
                  <div className="mt-6">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label htmlFor="firstName" className="block mb-1 text-sm font-medium text-gray-700">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          value={profile.firstName}
                          onChange={e => handleProfileChange("firstName", e.target.value)}
                          className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block mb-1 text-sm font-medium text-gray-700">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          value={profile.lastName}
                          onChange={e => handleProfileChange("lastName", e.target.value)}
                          className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md"
                          required
                        />
                      </div>
                    </div>
                    <label htmlFor="username" className="block mb-1 text-sm font-medium text-gray-700">
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={profile.username || ""}
                      disabled
                      className="px-3 py-2 mb-3 w-full text-sm text-gray-500 bg-gray-200 rounded-md cursor-not-allowed"
                      placeholder="Username not available"
                    />
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700">
                        Email Address <span className="text-black">*</span>
                      </label>
                      {profile.emailConfirmed ? (
                        <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                          Verified
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                            Unverified
                          </span>
                          <button
                            type="button"
                            onClick={handleResendEmail}
                            disabled={isResendingEmail}
                            className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          >
                            {isResendingEmail ? "Resending..." : "Resend"}
                          </button>
                        </div>
                      )}
                    </div>
                    <input
                      id="emailAddress"
                      type="email"
                      value={profile.emailAddress}
                      onChange={e => handleProfileChange("emailAddress", e.target.value)}
                      className="px-3 py-2 mb-3 w-full text-sm text-gray-600 bg-gray-100 rounded-md"
                      required
                    />
                    <label htmlFor="msisdn" className="block mb-1 text-sm font-medium text-gray-700">
                      Phone Number <span className="text-black">*</span>
                    </label>
                    <input
                      id="msisdn"
                      type="text"
                      value={profile.msisdn}
                      onChange={e => handleProfileChange("msisdn", e.target.value)}
                      className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md"
                    />
                  </div>
                  <CountryCurrencyDisplay />
                  <div className="flex gap-2 justify-end mt-4">
                    <button
                      type="submit"
                      disabled={profileSaving}
                      className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {profileSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </div>

              {/* Company Information - Only show for certain organization roles */}
              {!shouldHideCompanySection() && (
                <div className="p-4 bg-white rounded-xl border border-gray-600">
                  <h2 className="mb-3 text-sm font-semibold">Company Information</h2>

                  {organizationLoading ? (
                    <div className="mt-6 text-center text-gray-500">Loading organization details...</div>
                  ) : (
                    <form onSubmit={handleOrganizationSave} className="mt-6">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={organization.name}
                            onChange={e => handleOrganizationChange("name", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            Company Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={organization.code}
                            onChange={e => handleOrganizationChange("code", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            Contact Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={organization.contactEmail}
                            onChange={e => handleOrganizationChange("contactEmail", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Alternate Email</label>
                          <input
                            type="email"
                            value={organization.alternateEmail}
                            onChange={e => handleOrganizationChange("alternateEmail", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Contact Phone</label>
                          <input
                            type="text"
                            value={organization.contactMsisdn}
                            onChange={e => handleOrganizationChange("contactMsisdn", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Website URL</label>
                          <input
                            type="url"
                            value={organization.webUrl}
                            onChange={e => handleOrganizationChange("webUrl", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block mb-1 text-sm font-medium text-gray-700">Address</label>
                          <input
                            type="text"
                            value={organization.address}
                            onChange={e => handleOrganizationChange("address", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">City</label>
                          <input
                            type="text"
                            value={organization.city}
                            onChange={e => handleOrganizationChange("city", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Region</label>
                          <input
                            type="text"
                            value={organization.region}
                            onChange={e => handleOrganizationChange("region", e.target.value)}
                            className="px-3 py-2 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end mt-4">
                        <button
                          type="submit"
                          disabled={organizationSaving}
                          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {organizationSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Security Settings */}
              <div className="p-4 bg-white rounded-xl border border-gray-600">
                <h2 className="mb-3 text-sm font-semibold">Security Settings</h2>

                <form onSubmit={handlePasswordSave} className="mt-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="currentPassword" className="block mb-1 text-sm font-medium text-gray-700">
                        Current Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="currentPassword"
                          type={passwordVisibility.currentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={e => handlePasswordChange("currentPassword", e.target.value)}
                          placeholder="Enter current password"
                          className="px-3 py-2 pr-10 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility("currentPassword")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {passwordVisibility.currentPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="block mb-1 text-sm font-medium text-gray-700">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          type={passwordVisibility.newPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={e => handlePasswordChange("newPassword", e.target.value)}
                          placeholder="Enter new password (min 8 characters)"
                          className="px-3 py-2 pr-10 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility("newPassword")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {passwordVisibility.newPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block mb-1 text-sm font-medium text-gray-700">
                        Confirm New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          type={passwordVisibility.confirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={e => handlePasswordChange("confirmPassword", e.target.value)}
                          placeholder="Confirm new password"
                          className="px-3 py-2 pr-10 w-full text-sm text-gray-600 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility("confirmPassword")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {passwordVisibility.confirmPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end mt-4">
                    <button
                      type="submit"
                      disabled={passwordSaving}
                      className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {passwordSaving ? "Updating..." : "Update Password"}
                    </button>
                    {passwordSuccess && <span className="self-center text-green-600">Password updated!</span>}
                    {passwordError && <span className="self-center text-red-600">{passwordError}</span>}
                  </div>
                </form>
              </div>
            </div>

            {/* Danger Zone - Delete Account */}
            <div className="p-6 mt-6 bg-white rounded-xl border border-red-200">
              <h3 className="text-lg font-semibold text-red-600">Danger Zone</h3>
              <p className="mt-1 text-sm text-gray-500">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <button
                onClick={() => setShowDeleteAccountModal(true)}
                className="mt-4 px-4 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </HPageContainer>

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 bg-white rounded-lg max-w-md">
            <h3 className="text-lg font-semibold text-red-600">Delete Account</h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete your account? This action cannot be undone. All your data will be
              permanently removed.
            </p>
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => setShowDeleteAccountModal(false)}
                disabled={isDeletingAccount}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isDeletingAccount ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfile;
